import type { SubscriptionTier } from '../types';

/**
 * VolumeControl - Volume adjustment with tier-based controls
 *
 * Features:
 * - Free: On/Off toggle only
 * - Pro: Smooth volume slider (0-100%)
 * - Mute functionality
 */

interface VolumeControlProps {
  value: number; // 0-1
  onChange: (volume: number) => void;
  disabled?: boolean;
  tier: SubscriptionTier;
}

export function VolumeControl({
  value,
  onChange,
  disabled = false,
  tier,
}: VolumeControlProps): JSX.Element {
  const isPro = tier === 'pro';
  const percentage = Math.round(value * 100);

  if (isPro) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700">
            Volume
          </label>
          <span className="text-sm text-gray-600">{percentage}%</span>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-lg">🔈</span>

          <input
            type="range"
            min="0"
            max="100"
            step="5"
            value={percentage}
            onChange={(e) => onChange(parseInt(e.target.value) / 100)}
            disabled={disabled}
            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
          />

          <span className="text-lg">🔊</span>
        </div>
      </div>
    );
  }

  // Free tier: On/Off toggle
  const isOn = value > 0;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          Volume
        </label>
        <span className="text-xs text-gray-500">Free tier</span>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onChange(isOn ? 0 : 0.7)}
          disabled={disabled}
          className={`
            flex-1 p-3 rounded-lg border-2 transition-all
            ${isOn ? 'border-emdr-blue bg-emdr-blue/10' : 'border-gray-300 bg-white'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-emdr-blue cursor-pointer'}
          `}
        >
          <div className="flex items-center justify-center gap-2">
            <span className="text-xl">{isOn ? '🔊' : '🔇'}</span>
            <span className="text-sm font-medium text-gray-900">
              {isOn ? 'On' : 'Off'}
            </span>
          </div>
        </button>
      </div>

      <div className="text-xs text-gray-500 text-center pt-1">
        Upgrade to Pro for volume control
      </div>
    </div>
  );
}
